import { ModalChooseIhiNumber } from '@/components/PatientPageV2/components/IhiVerifyAndUpdateModals/components/ModalChooseIhiNumber/ModalChooseIhiNumber';
import { ModalSelectMultipleIhiFields } from '@/components/PatientPageV2/components/IhiVerifyAndUpdateModals/components/ModalSelectMultipleIhiFields/ModalSelectMultipleIhiFields';
import { ModalUnableToVerifyIhi } from '@/components/PatientPageV2/components/IhiVerifyAndUpdateModals/components/ModalUnableToVerifyIhi/ModalUnableToVerifyIhi';
import type { HandleUpdateIhiDetailsArgs } from '@/components/PatientPageV2/components/IhiVerifyAndUpdateModals/hooks/usePatientIhi';
import { getIHINumberFromUrl } from '@/components/PatientPageV2/components/IhiVerifyAndUpdateModals/IhiVerifyAndUpdateModals.utils';
import { usePostPatientIhi } from '@/hooks/patients/usePostPatientIhi';
import { useVerifyPatientIhi } from '@/hooks/patients/useVerifyPatientIhi';
import { useAppStore } from '@/state-management';
import { toast } from '@montugroup/design-system';
import type { GetPatientIhiSuccessResponseDto } from '@montugroup/pms-api-contracts';
import { DateTime } from 'luxon';
import { useModal } from 'mui-modal-provider';
import { useState } from 'react';

export type IhiServiceResponse = GetPatientIhiSuccessResponseDto;

/**
 * Custom hook for IHI verification as well as updated patient records based on the information from the IHI Service
 * @returns - verifyIhi method
 */
export function usePatientIhi() {
  const setPatientBasicInfo = useAppStore.use.setPatientBasicInfo();
  const setPatientIdentificationInfo = useAppStore.use.setPatientIdentificationInfo();
  const setPatientIhiVerification = useAppStore.use.setPatientIhiVerification();
  const patientId = useAppStore.use.getPatientId()();
  const patientDetails = useAppStore((state) => state.getPatient());

  const defaultState = {
    isPending: false,
    isError: false,
    isSuccess: false,
  };

  const [hookStatus, setHookStatus] = useState(defaultState);
  const { postPatientIhi } = usePostPatientIhi();
  const { verifyPatientIhi } = useVerifyPatientIhi();
  const { showModal } = useModal();

  const handleUpdateIhiDetails = async ({
    patientIHISavePayload,
    updateProfile,
    reasonForUpdate,
  }: HandleUpdateIhiDetailsArgs) => {
    try {
      setHookStatus((prev) => ({ ...prev, isPending: true }));

      setPatientIdentificationInfo({
        ihiNumberStatus: null,
        ihiRecordStatus: null,
      });

      await postPatientIhi({
        patientId: Number(patientId),
        patientIHI: patientIHISavePayload,
        updateProfile,
        // Include a 'reasonForUpdate' into the payload if it exists
        ...(reasonForUpdate && { reasonForUpdate }),
      });

      if (patientIHISavePayload.ihiStatus === null && patientIHISavePayload.ihiRecordStatus === null) {
        toast.warning("Patient's (Unverified) IHI successfully updated.");
      } else {
        toast.success("Patient's IHI successfully updated.");
      }

      setPatientBasicInfo({
        ...(updateProfile?.firstName ? { firstName: updateProfile.firstName } : null),
        ...(updateProfile?.lastName ? { lastName: updateProfile.lastName } : null),
        ...(updateProfile?.dob
          ? {
              dob: DateTime.fromJSDate(updateProfile.dob).toUTC().startOf('day').toFormat("yyyy-MM-dd'T'00:00:00'Z'"),
            }
          : null),
        ...(updateProfile?.gender ? { gender: updateProfile.gender } : null),
      });

      setPatientIdentificationInfo({
        ihiNumber: getIHINumberFromUrl(patientIHISavePayload.patient.ihiNumber),
        ihiNumberStatus: patientIHISavePayload.ihiStatus,
        ihiRecordStatus: patientIHISavePayload.ihiRecordStatus,
        ...(updateProfile?.medicareNumber ? { medicareNumber: updateProfile.medicareNumber } : null),
        ...(updateProfile?.medicareRefNumber ? { medicareRefNumber: updateProfile.medicareRefNumber } : null),
      });

      setHookStatus((prev) => ({ ...prev, isPending: false, isSuccess: true }));
    } catch (err) {
      let errorMessage = 'Failed to update patient IHI. Please try again later.';
      if (err instanceof Error) {
        errorMessage = err.message;
      }

      setHookStatus((prev) => ({ ...prev, isError: true }));

      toast.error(`IHI Error: ${errorMessage}`);
    }
  };

  const displayModal = (ihiServiceResponse: IhiServiceResponse | string, newIhiNumber?: string | null) => {
    let ModalComponent;
    const ihiServiceData = typeof ihiServiceResponse !== 'string' ? ihiServiceResponse?.response : null;

    const patientDetailsInPMS = {
      FirstName: patientDetails.basicInfo?.firstName,
      LastName: patientDetails.basicInfo.lastName,
      gender: patientDetails.basicInfo.gender,
      DoB: patientDetails.basicInfo.dob,
      medicareNumber: patientDetails.identificationInfo.medicareNumber,
      medicare_ref_number: patientDetails.identificationInfo.medicareRefNumber,
      ihi_number: newIhiNumber ?? patientDetails.identificationInfo.ihiNumber,
    };

    // Determine IHI verification status and whether the IHI number is new
    const isIhiVerified = ihiServiceData?.ihiRecordStatus === 'Verified';
    const existingIhiNumber = patientDetails.identificationInfo.ihiNumber;
    const urlIhiNumber = getIHINumberFromUrl(ihiServiceData?.patient?.ihiNumber);
    const isNewIhiNumber =
      patientDetailsInPMS.ihi_number !== urlIhiNumber && patientDetailsInPMS.ihi_number !== existingIhiNumber;

    // Determine initial modal if response is a string
    if (typeof ihiServiceResponse === 'string') {
      ModalComponent = ModalUnableToVerifyIhi;
    }

    // Determine the appropriate modal to show
    if (ihiServiceData) {
      ModalComponent = isIhiVerified && isNewIhiNumber ? ModalChooseIhiNumber : ModalSelectMultipleIhiFields;
    }

    const modalProps = {
      'data-testid': 'ihi-verification-modal',
      ...(isNewIhiNumber ? { unverifiedIhiNumber: newIhiNumber } : null),
      ...(ihiServiceData ? { patientVerifyIhiResponseData: ihiServiceData } : null),
      patientDetailsInPMS,
      disableEscapeKeyDown: true,
      handleUpdateIhiDetails,
      handleModalClose: () => {
        setHookStatus(() => defaultState);

        if (patientDetailsInPMS.ihi_number !== patientDetails.identificationInfo.ihiNumber) {
          setPatientIdentificationInfo({ ihiNumber: patientDetails.identificationInfo.ihiNumber });
        }

        setPatientIhiVerification(false);
        modal.hide();
      },
    };

    const modal = showModal(ModalComponent as React.ComponentType, modalProps, { hideOnClose: false });
  };

  const verifyIhi = async (ihi_number?: string | null) => {
    const ihiNumber = ihi_number;

    const responseData = await verifyPatientIhi({
      ihiNumber,
      patientId: Number(patientId),
    });

    if (typeof responseData === 'string') {
      toast.error(`IHI Error: ${responseData}`);
      return;
    }

    displayModal(responseData, ihiNumber);
  };
  return {
    verifyIhi,
    ...hookStatus,
  };
}
